'use client';

import clsx from 'clsx';
import { useId, type ChangeEvent } from 'react';
import styles from './RadioButton.module.scss';

export type RadioButtonSize = 'lg' | 'md';

const RADIO_SIZE_CLASS: Record<RadioButtonSize, string> = {
  md: styles.size_md,
  lg: styles.size_lg,
};

export interface RadioButtonProps {
  /** 라디오 그룹 이름 (같은 `name`끼리 하나만 선택) */
  name: string;
  value: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  size?: RadioButtonSize;
  label?: string;
  /** Checkbox의 `caption`과 동일 역할 — 있으면 라벨과 함께 `labelBlock`으로 묶음 */
  caption?: string;
  /** `true`이면 루트가 `span`이며 링·닷만 렌더 (외부 `<label htmlFor={id}>`와 조합) */
  embedded?: boolean;
  className?: string;
  labelClassName?: string;
  id?: string;
}

export default function RadioButton({
  name,
  value,
  checked,
  defaultChecked,
  onChange,
  disabled = false,
  size = 'md',
  label,
  caption,
  embedded = false,
  className,
  labelClassName,
  id: idProp,
}: RadioButtonProps) {
  const reactId = useId();
  const inputId = idProp ?? `radio-${reactId.replace(/:/g, '')}`;
  const isControlled = checked !== undefined;

  const wrapperClass = clsx(styles.wrapper, embedded && styles.wrapper_embedded, RADIO_SIZE_CLASS[size], caption && styles.with_caption, disabled && styles.disabled, className);

  const control = (
    <span className={styles.control}>
      <input
        id={inputId}
        type="radio"
        name={name}
        value={value}
        checked={isControlled ? checked : undefined}
        defaultChecked={!isControlled ? defaultChecked : undefined}
        disabled={disabled}
        onChange={onChange}
        className={styles.input}
      />
      <span className={styles.ring} aria-hidden>
        <span className={styles.dot} />
      </span>
    </span>
  );

  if (embedded) {
    return <span className={wrapperClass}>{control}</span>;
  }

  return (
    <label htmlFor={inputId} className={wrapperClass}>
      {control}
      {caption ? (
        <div className={styles.labelBlock}>
          {label && (
            <span className={clsx(styles.label, labelClassName)}>
              {label}
            </span>
          )}
          <span className={styles.caption}>{caption}</span>
        </div>
      ) : (
        label && (
          <span className={clsx(styles.label, labelClassName)}>
            {label}
          </span>
        )
      )}
    </label>
  );
}
